/**
 * /platform/learning — SEO landing page for Learning & Courses.
 */
import React from 'react';
import {
  BookOpen, Video, PlayCircle, ClipboardCheck, Award, Bookmark,
  Layers, Sparkles, Users, Clock, Bot, Calculator, TrendingUp,
  UserCheck, GraduationCap, Rocket,
} from 'lucide-react';
import PlatformFeaturePage from './PlatformFeaturePage';

const LearningPage = () => (
  <PlatformFeaturePage
    slug="learning"
    eyebrow="Learning & Courses"
    title="Freeze-dry"
    titleAccent="learning that respects your time."
    subtitle="Structured, tier-organized lessons written by working operators — the exact playbooks we use in production, not filler."
    seo={{
      title: 'Freeze Dry Courses & Lessons — Freeze Dry Academy',
      description: 'Self-paced freeze-dry courses covering cycle science, machine ops, business planning, and troubleshooting. Free tier available.',
      keywords: 'freeze dry courses, freeze drying training, freeze dryer classes, freeze drying business course',
    }}
    highlights={[
      { icon: PlayCircle, label: 'Self-paced video + text' },
      { icon: Bookmark, label: 'Bookmark & resume' },
      { icon: ClipboardCheck, label: 'Quizzes on every module' },
      { icon: Award, label: 'Earn badges' },
    ]}
    primaryCta={{ label: 'Start learning free', to: '/login?mode=signup' }}
    secondaryCta={{ label: 'Browse pricing', to: '/pricing' }}
    howItWorks={[
      { title: 'Pick a course', body: 'Filter by tier (Free / Pro) or by what you\'re trying to solve — cycle tuning, machine choice, business setup.' },
      { title: 'Watch, read, quiz', body: 'Each module mixes 3-8 minute videos, written explanations, printable cheat-sheets, and short quizzes to lock the concept in.' },
      { title: 'Track & apply', body: 'Progress bars, in-app notifications when a course you follow gets a new lesson, and direct links into the calculators to try the math.' },
    ]}
    features={[
      { icon: Layers,     title: 'Tier-organized curriculum', body: 'Beginner, intermediate and advanced tracks that build on each other — no rummaging through 4-hour YouTube playlists.' },
      { icon: Video,      title: 'Real production footage',   body: 'We film in real freeze-drying rooms with real product. What you see is what happens when the door closes.' },
      { icon: Bookmark,   title: 'Bookmark & resume',         body: 'Half-way through a module? Bookmark it and you\'ll drop right back in from any device — with a notification when it\'s updated.' },
      { icon: ClipboardCheck, title: 'Quizzes + checklists',  body: 'Every lesson ends with a knowledge check. Some ship with printable pre-batch checklists you can pin next to the machine.' },
      { icon: Award,      title: 'Certificate badges',        body: 'Complete a track and earn a badge that shows up on your profile in the Community forum. Free operator street cred.' },
      { icon: Sparkles,   title: 'Ships often',              body: 'We publish new lessons often — driven by community questions and things we get asked on consulting calls.' },
    ]}
    personas={[
      { icon: Rocket,        title: 'Brand-new hobbyists',    body: 'Bought your first machine and don\'t know what a "collector" is yet? Start with the Free tier — everything you need for your first 10 batches.' },
      { icon: TrendingUp,    title: 'Side-hustlers going pro',body: 'Made your first sale on Facebook Marketplace and want to scale to a licensed cottage kitchen? Pro tier walks you through the whole path.' },
      { icon: GraduationCap, title: 'Full-scale operators',   body: 'Running 4+ machines? Advanced cycle-tuning, batch-cost engineering and multi-unit scheduling live in the Pro Advanced modules.' },
    ]}
    faqs={[
      { q: 'Do I need to pay to see the courses?',
        a: 'No — every course has a Free preview lesson, and the entire Foundations track is free forever. Pro unlocks the deep advanced material.' },
      { q: 'How is this different from YouTube?',
        a: 'YouTube shows you what worked once for one person. Our courses are structured curricula written by experienced operators, updated when best practices change, and paired with checklists + calculators so you can actually apply what you learn.' },
      { q: 'Can I download the lessons for offline?',
        a: 'The written material + checklists yes (via print / PDF export). Videos stream from our CDN — you\'ll need connectivity to watch them.' },
      { q: 'Will you notify me when new lessons drop?',
        a: 'Yes. Bookmark a course and you\'ll get an in-app notification (and a device push if you enable it) the moment a new lesson lands.' },
    ]}
    related={[
      { slug: 'ai-assistant',   title: 'AI Assistant',      icon: Bot,        blurb: 'Ask any freeze-dry question, cited to the source lesson.' },
      { slug: 'calculators',    title: 'Calculators',        icon: Calculator, blurb: 'Turn every lesson into real batch math.' },
      { slug: 'community',      title: 'Community Forum',    icon: Users,      blurb: 'Ask the class — real replies from real operators.' },
    ]}
    finalCtaTitle="Ready to learn what actually works?"
    finalCtaBody="The Foundations track is free — sign up and you can watch the first lesson in under a minute."
  />
);

export default LearningPage;
